<template>
  <div class="page-builder-view">
    <SectionsBuilderBanner v-if="company" :company="company" />
    <section v-if="company" class="page-builder-view__content-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-8">
            <div class="row">
              <div class="col-md-12">
                <!-- Overview Block -->
                <BlocksCard>
                  <h2 class="__heading">
                    Overview
                  </h2>
                  <p v-if="company.overview" v-html="formatTextAreaContent(company.overview)" />
                </BlocksCard>

                <!-- Promotions Block -->
                <div v-if="Array.isArray(company.promotions) && company.promotions.length > 0" class="page-builder-view__promotion-wrapper">
                  <BlocksCard v-for="(promotion, key) in company.promotions" :key="`promotion-${key}`" promotion>
                    <h2 class="__heading">
                      {{ promotion.name }}
                    </h2>
                    <p><strong>{{ promotion.description }}</strong></p>
                    <div v-if="promotion.actionButton" class="__action-button">
                      <ElementsButtonsRegular :label="promotion.actionButton.label" secondary size="md" :button-link="promotion.actionButton.link" />
                    </div>
                  </BlocksCard>
                </div>

                <!-- Gallery Block -->
                <BlocksGallery v-if="Array.isArray(company.gallery.data) && company.gallery.data.length > 0" heading="Gallery" :images="company.gallery.data" click-to-view-full-screen :alt-text="`${companyName} gallery image`" />
              </div>
            </div>
          </div>
          <div class="col-lg-4">
            <BlocksCard class="__builder-wrapper">
              <div class="__builder-logo-wrapper">
                <nuxt-img :src="typeof company.logoUrl === 'string' ? company.logoUrl : ''" alt="Development logo" fit="cover" width="160" height="auto" />
              </div>
              <div class="__builder-contact-info-wrapper">
                <div v-if="company.address" class="__address">
                  {{ company.address.streetNumber }} {{ company.address.addressLine1 }}, {{ company.address.addressLine2 }} {{ company.address.city }} {{ company.address.postalCode }}
                </div>
                <div class="__address-description">
                  {{ companyName }}
                </div>
                <div v-for="(phoneNumber, index) in company.phoneNumbers.data" :key="`phone-number-${index}`" class="__phone __icon-content" @click="triggerPixelLeadEvent('phone')">
                  <img v-lazy-load  :data-src="phoneIcon" alt="Phone icon">
                  <a :href="`tel:${phoneNumber}`">{{ phoneNumber }}</a>
                </div>
                <ElementsButtonsRegular text size="md" :label="`Email ${contactLabel}`" :append-icon="emailIcon" @click="isEnquiryOpen = true" />
                <ElementsButtonsRegular text size="md" :label="`Chat with ${contactLabel}`" :append-icon="messageIcon" :loading="!!isChatInitiating" @click="triggerInitiateChat" />
              </div>

              <!-- Action buttons -->
              <div class="__action-button">
                <ElementsButtonsRegular secondary label="View Our Listings" size="sm" :button-link="`/builder/${company.slug}/listings`" :analytics-click-event-data="{entity: 'Company', buttonAction: `view ${company.slug} listings`, target: '_self', displayedLocation: 'builder view'}" />
                <ElementsButtonsRegular v-if="company.websiteUrl" secondary label="View Our Website" size="sm" link-target="_blank" :button-link="company.websiteUrl" :analytics-click-event-data="{entity: 'Company', buttonAction: `view ${company.slug} website`, target: '_blank', displayedLocation: 'builder view'}" />
              </div>
            </BlocksCard>

            <!-- Key Information -->
            <BlocksCard v-if="Array.isArray(company.keyInformation.data) && company.keyInformation.data.length > 0">
              <h2 class="__heading __underlined">
                Key Information
              </h2>
              <ul v-if="company.keyInformation.data" class="__check-list __bold">
                <li v-for="(keyInfoItem, key) in company.keyInformation.data" :key="`key-info-${key}`">
                  {{ keyInfoItem }}
                </li>
              </ul>
            </BlocksCard>

            <BlocksCard v-if="Array.isArray(company.cities.data) && company.cities.data.length > 0" class="builder-cities-block" :class="{'--showing-more': isViewMore}">
              <h2 class="__heading __underlined">
                Cities
              </h2>
              <div class="description-block__inner" :style="!isViewMore ? `max-height: 320px;` : ''">
                <ul class="__check-list">
                  <li v-for="(city, key) in company.cities.data" :key="`city-${key}`" style="text-transform: capitalize" class="__city-list-item" @click="showCityListings(city)">
                    {{ city }}
                  </li>
                </ul>
                <div v-if="company.cities.data.length > 10" id="fadeOverlay" class="fade" />
              </div>
              <div v-if="company.cities.data.length > 10" class="__view-more-btn" @click="isViewMore = !isViewMore">
                See {{ isViewMore ? 'Less' : 'More' }}
                <img v-lazy-load  :data-src="chevron" alt="Down arrow icon">
              </div>
            </BlocksCard>
          </div>
        </div>
      </div>

      <!-- Email Enquiry -->
      <CommonEmailEnquiry v-model:is-open="isEnquiryOpen" enquiry-type="builder" :enquiring-entity-id="company.did" :tracking-event-name="company.name" />
    </section>
    <SectionsListingCategorized :key="`featured-listing`" :heading="`${company.name} - Our Featured listings`" disable-load-on-server :custom-listing-params="{filterBy: `company.slug:=${company.slug}`, perPage: 12}" sub-heading="Top Picks" :view-more-company-slug="company.slug" view-more-label="View All" :show-category-filters="true" :hidden-filters="['developments', 'builders']" :display-location="`${company.slug} builder page`" />

    <LazyAdAdBar ad-display-location="builder_page_banner" />
  </div>
</template>

<script setup lang="ts">
import {ComputedRef, Ref} from 'vue';
import phoneIcon from '~/assets/images/icons/phone.webp';
import {useFetch, useRoute, useRouter} from '#app';
import {Company} from '~/@types/company';
import globalApiHeaders from '~/composables/globalApiHeaders';
import {ApiReturn} from '~/@types/common';
import ListingSearchQueryHandler from '~/composables/listingSearchQuery';
import chevron from 'assets/images/icons/chevron-down.webp';
import emailIcon from '~/assets/images/icons/email.png';
import messageIcon from '~/assets/images/icons/message.webp';
import {useAuthUserStore} from '~/store/authUser';
import {useLayoutStore} from '~/store/layout';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const route = useRoute();
const config = useRuntimeConfig();
const company: Ref<Company | null> = ref(null);
const {$analyticsTrack} = useNuxtApp();
const isViewMore: Ref<boolean> = ref(false);
const router = useRouter();
const isChatInitiating: Ref<boolean> = ref(false);
const {$initiateChat}: any = useNuxtApp();
const authUserStore = useAuthUserStore();
const layoutStore = useLayoutStore();
const {$metaPixelTrackEvent} = useNuxtApp();
const isEnquiryOpen: Ref<boolean> = ref(false);

// DATA Fetching
//----------------------------------------------------------------------------------------------------------------------
const {data} = await useFetch(() => `/public/companies/show/${route.params.slug}`,
  { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
);
if (data.value) {
  if ((data.value as ApiReturn).code === 200 && (data.value as ApiReturn).data) {
    // Data is returned
    company.value = (data.value as ApiReturn).data as Company;
  } else if ((data.value as ApiReturn).code === 404) {
    // No data is returned. Page not found
    throw createError({statusCode: 404, message: 'Company not found', fatal: true});
  } else {
    // Something went wrong
    throw createError({statusCode: 500, message: 'Something went wrong', fatal: true});
  }
}

/**
 * Trigger initiate chat
 */
async function triggerInitiateChat () {
  if (!company.value) return;
  triggerPixelLeadEvent('chat');

  isChatInitiating.value = true;

  authUserStore.setChatInitiationData({
    location: 'company',
    locationDid: company.value.did,
    url: window.location.href,
    roomId: null,
  });

  if (authUserStore.user) {
    const response = await $initiateChat('company', company.value.did);
    if (response) {
      authUserStore.setChatInitiationData({
        location: 'company',
        locationDid: company.value.did,
        url: window.location.href,
        roomId: response.code === 200 ? response.data.toString() : null,
        hasError: response.code !== 200,
      });
    }

    setTimeout(() => {
      layoutStore.setChatOpen(true);
      isChatInitiating.value = false;
    }, 1500);
  } else {
    router.push('/auth/login');
    isChatInitiating.value = false;
  }
}

/**
 * Trigger pixel event
 */
function triggerPixelLeadEvent (type: 'phone' | 'chat') {
  if (!company.value) return;
  // Track meta event
  $metaPixelTrackEvent('Lead', {
    content_name: company.value.name,
    content_category: company.value.profileType ?? 'Builder',
    content_ids: [company.value.did],
    content_type: 'Company',
    lead_type: type,
  });
}

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const seoMetaData = getSeoMeta();
useSeoMeta({
  title: seoMetaData.title,
  description: seoMetaData.description,
  ogTitle: seoMetaData.title,
  ogDescription: seoMetaData.description,
  ogImage: seoMetaData.imageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: seoMetaData.title,
  twitterDescription: seoMetaData.description,
  twitterImage: seoMetaData.imageUrl,
});

function getSeoMeta (): {title: string, description: string, robots: string, imageUrl: string} {
  const companyName = company.value ? company.value.displayName : 'Builder';
  const seoImageUrl = company.value?.bannerUrl ?? 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg';
  if (company.value && company.value.seo) {
    const { title, description, imageUrl, robots } = company.value.seo;
    return {
      title: title || `${companyName} | New Homes - Discover ${companyName}'s House and Land Packages, Designs`,
      description: description || `Find your new home with ${companyName}. We have a huge range of new homes available in ${companyName}.`,
      robots: robots || 'index, follow',
      imageUrl: imageUrl || seoImageUrl,
    };
  } else {
    return {
      title: `${companyName} | New Homes - Discover ${companyName}'s House and Land Packages, Designs`,
      description: `Find your new home with ${companyName}. We have a huge range of new homes available in ${companyName}.`,
      robots: 'index, follow',
      imageUrl: seoImageUrl,
    };
  }
}

/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  triggerAnalyticsTracking();
});

/**
 * Company Name
 */
const companyName: ComputedRef<string> = computed(() => {
  if (company.value) {
    return company.value.displayName || company.value.name;
  }
  return '';
});

const contactLabel: ComputedRef<string> = computed(() => {
  if (company.value) {
    switch (company.value.profileType) {
      case 'Builder':
        return 'Builder';
      case 'Developer':
        return 'Developer';
      case 'Real Estate':
        return 'Agent';
    }
  }
  return '';
});

/**
 * Show city listing
 */
function showCityListings (cityName: string) {
  if (!company.value) return;

  ListingSearchQueryHandler.set({keyword: cityName}, `/builder/${company.value.slug}/listings`);
}

/**
 * Trigger analytics tracking
 */
function triggerAnalyticsTracking () {
  if (!company.value) return;

  // Track analytics event
  $analyticsTrack({
    action: 'view',
    entity: 'Company',
    property: 'builder',
    entitySlug: company.value.slug,
    displayedLocation: 'builder page',
    eventData: {
      userIntention: 'active',
      component: 'builder page',
    },
  });

  // Track meta event
  $metaPixelTrackEvent('ViewContent', {
    content_name: company.value.name,
    content_category: company.value?.profileType ?? 'Builder',
    content_ids: [company.value.did],
    content_type: 'Company',
  });
}
</script>

<style lang="scss">
.page-builder-view {
  background: #f4f4ff;

  .page-builder-view__content-section {
    width: 100%;
    background: #f4f4ff;
    position: relative;
  }

  .__builder-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .__builder-logo-wrapper {
      width: 185px;
      height: 185px;
      border-radius: 50%;
      overflow: hidden;
      background: #fff;
      border: 1px solid #e0e0e0;
      padding: 16px;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        max-width: 100%;
        max-height: 100%;
      }
    }

    .__builder-contact-info-wrapper {
      margin-top: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .__address {
        font-weight: 400;
        color: rgb(var(--text-default));
      }

      .__address-description {
        font-size: 14px;
        margin-top: 5px;
        margin-bottom: 12px;
        display: block;
        color: rgba(var(--text-default), 0.5);
      }

      .__icon-content {
        margin-top: 5px;
        display: flex;
        align-items: center;

        &:not(:last-child) {
          margin-top: 18px;
        }

        img {
          width: 15px;
          height: auto;
          margin-right: 5px;
        }

        &.__phone {
          margin-bottom: 12px;

          a {
            text-decoration: none;
            color: rgb(var(--text-default));
            transition: all 0.2s ease-in-out;
          }

          &:hover {
            a {
              color: rgb(var(--text-dark));
              transition: all 0.2s ease-in-out;
            }
          }
        }

        &.__message {
          .el-button-regular {
            background-color: transparent;
            box-shadow: unset;
            color: rgb(var(--primary));

            .__button-label {
              font-size: 14px;
              font-weight: 500;
            }
          }
        }

        &:hover {
          img {
            scale: 1.1;
            transition: all 0.2s ease-in-out;
          }
        }
      }
    }
  }

  .__city-list-item {
    cursor: pointer;
    transition: scale 0.2s ease-in-out;

    &:hover {
      scale: 1.03;
      transition: scale 0.2s ease-in-out;
    }
  }

  .__top-border {
    border-top: 1px solid rgb(0, 0, 0, 0.1);
  }



  .builder-cities-block {
    .description-block__inner {
      overflow: hidden;
    }

    .__view-more-btn {
      width: 100%;
      text-align: center;
      font-size: 14px;
      color: rgb(var(--text-dark));
      cursor: pointer;
      transform: scale(1);
      transition: all 0.2s ease-in-out;

      img {
        width: 15px;
        margin-left: 4px;
        transition: all 0.2s ease-in-out;
      }

      &:hover {
        transform: scale(1.05);
        transition: all 0.2s ease-in-out;
      }
    }

    &.--showing-more {
      .description-block__inner {
        .fade {
          height: 0;
        }
      }

      .__view-more-btn {
        img {
          transform: rotate(180deg);
          transition: all 0.2s ease-in-out;
        }
      }
    }
  }
}
</style>
